import { Box, Typography } from '@mui/material';

import type { StepperProps, StepperStep } from 'components/stepper/type';

import {
  STEP_ACTIVE_COLOR,
  STEP_INACTIVE_COLOR,
  CONNECTOR_ACTIVE_COLOR,
  CONNECTOR_INACTIVE_COLOR,
  NODE_SIZE,
  NODE_SIZE_ACTIVE,
  CONNECTOR_NODE_GAP,
  CONNECTOR_HEIGHT,
  LABEL_TOP_GAP,
  LABEL_ACTIVE_COLOR,
  LABEL_INACTIVE_COLOR,
} from 'styles/stepper';

function normalizeSteps(steps: StepperStep[] | string[]): StepperStep[] {
  return steps.map(s => (typeof s === 'string' ? { label: s } : s));
}

function getStepLabelTextAlign(index: number, lastIndex: number): 'left' | 'right' | 'center' {
  if (index === 0) {
    return 'left';
  }

  if (index === lastIndex) {
    return 'right';
  }

  return 'center';
}

const connectorSx = (completed: boolean, side: 'left' | 'right') => ({
  flex: 1,
  minWidth: 4,
  height: CONNECTOR_HEIGHT,
  ...(side === 'left'
    ? { marginRight: `${CONNECTOR_NODE_GAP}px` }
    : { marginLeft: `${CONNECTOR_NODE_GAP}px` }),
  borderRadius: '999px',
  backgroundColor: completed ? CONNECTOR_ACTIVE_COLOR : CONNECTOR_INACTIVE_COLOR,
});

const Stepper = ({ steps, activeStep, className, ariaLabel = 'Progress' }: StepperProps) => {
  const normalizedSteps = normalizeSteps(steps);
  const lastIndex = normalizedSteps.length - 1;

  return (
    <Box
      className={className}
      role="group"
      aria-label={ariaLabel}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        // Do not clip labels — Figma shows full "Verify Identity" / "Set Up Account" / "Find Expenses"
        overflow: 'visible',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          minHeight: NODE_SIZE_ACTIVE,
          maxWidth: '261px',
          marginLeft: '45px',
        }}
      >
        {normalizedSteps.map((step, index) => {
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;
          const isActiveOrCompleted = isActive || isCompleted;
          const nodeColor = isActiveOrCompleted ? STEP_ACTIVE_COLOR : STEP_INACTIVE_COLOR;
          const nodeSize = isActive ? NODE_SIZE_ACTIVE : NODE_SIZE;
          const isFirst = index === 0;
          const isLast = index === lastIndex;
          const connectorLeftCompleted = index > 0 && index - 1 < activeStep;
          const connectorRightCompleted = isCompleted;
          const stepKey = `${step.label}-${step.labelLine2 ?? ''}`;

          return (
            <Box
              key={stepKey}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
              }}
            >
              {!isFirst && <Box sx={connectorSx(connectorLeftCompleted, 'left')} />}
              <Box
                sx={{
                  width: nodeSize,
                  height: nodeSize,
                  borderRadius: '50%',
                  backgroundColor: nodeColor,
                  flexShrink: 0,
                }}
              />
              {!isLast && <Box sx={connectorSx(connectorRightCompleted, 'right')} />}
            </Box>
          );
        })}
      </Box>

      {/*
        Full Figma labels under each node. First label left-aligned under left node,
        middle centered, last right-aligned — keeps every word visible (no clip/ellipsis).
      */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          marginTop: `${LABEL_TOP_GAP}px`,
          overflow: 'visible',
        }}
      >
        {normalizedSteps.map((step, index) => {
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;
          const color = isCompleted || isActive ? LABEL_ACTIVE_COLOR : LABEL_INACTIVE_COLOR;
          const fontWeight = isActive ? 700 : 400;
          const textAlign = getStepLabelTextAlign(index, lastIndex);
          const textSx = {
            fontWeight,
            color,
            textAlign,
            lineHeight: 1.3,
            // Figma: single-line labels ("Verify Identity", etc.)
            whiteSpace: 'nowrap' as const,
          };
          const stepKey = `${step.label}-${step.labelLine2 ?? ''}`;

          return (
            <Box
              key={stepKey}
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'visible',
              }}
            >
              {step.labelLine2 !== undefined && step.labelLine2 !== '' ? (
                <Box sx={{ lineHeight: 1.3, textAlign }}>
                  <Typography component="p" variant="caption1_Regular" sx={textSx}>
                    {step.label}
                  </Typography>
                  <Typography component="p" variant="caption1_Regular" sx={textSx}>
                    {step.labelLine2}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  component="p"
                  variant="caption1_Regular"
                  sx={textSx}
                  {...(isActive && { 'aria-current': 'step' as const })}
                >
                  {step.label}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Stepper;
export type { StepperProps, StepperStep } from 'components/stepper/type';
